/**
 * Identity documents — what the citizen sees after uploading a passport or ID
 * card, and what they confirm. The profile changes only through the confirm.
 */

/**
 * What an identity document can fill in the profile. `name` is the LEGAL name
 * (as printed on the document, kept on the talent); `account_name` offers the
 * same name, as a person writes it, for the account — never pre-ticked over a
 * name already chosen.
 */
export const IDENTITY_DOCUMENT_APPLY_FIELDS = [
  'name',
  'account_name',
  'date_of_birth',
  'nationality',
  'passport',
] as const;
export type IdentityDocumentApplyField = (typeof IDENTITY_DOCUMENT_APPLY_FIELDS)[number];

/**
 * One line of the review: what the document says beside what the profile says.
 *
 * Values by key — `name`: "GIVEN NAMES SURNAME" as printed (the legal name);
 * `account_name`: "Given Names Surname" as a person writes it; `date_of_birth`:
 * YYYY-MM-DD; `nationality`: ISO alpha-2; `passport`: the expiry date, YYYY-MM-DD (applying
 * it records whether the citizen holds a valid passport). `profileValue` for
 * `passport` is `'yes'` / `'no'` / null (not answered).
 */
export interface IdentityDocumentReviewField {
  key: IdentityDocumentApplyField;
  documentValue: string;
  profileValue: string | null;
  /** The document's machine-readable zone confirmed it. */
  verified: boolean;
  /** Pre-ticked: the profile is empty there, or already says the same. */
  suggested: boolean;
  /** The profile already holds this very value: applying it changes nothing. */
  matchesProfile: boolean;
}

export interface IdentityDocumentReview {
  id: string;
  documentType: 'passport' | 'id_card';
  /** A machine-readable zone was found and every check digit held. */
  mrzValid: boolean;
  /** The last four characters only — the full number is never sent to a client. */
  documentNumberLast4: string | null;
  expiresAt: string | null;
  expired: boolean;
  fields: IdentityDocumentReviewField[];
}

export interface IdentityDocumentConfirmResult {
  applied: IdentityDocumentApplyField[];
}
